import { useCallback, useMemo, useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { useApi } from '@backstage/core-plugin-api';
import { ResponseErrorPanel } from '@backstage/core-components';
import {
  Accordion,
  AccordionGroup,
  AccordionPanel,
  AccordionTrigger,
  Alert,
  Button,
  ButtonIcon,
  Skeleton,
  Text,
} from '@backstage/ui';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import {
  SectionCard,
  useCatalogUserOptions,
} from '@internal/plugin-shared-react';
import { sonarqubeApiRef } from '../../../api/sonarqube-api';
import { AddGroupMemberDialog } from './AddGroupMemberDialog';
import {
  MemberToRemove,
  RemoveGroupMemberDialog,
} from './RemoveGroupMemberDialog';
import styles from './SonarQubeServiceGroupPage.module.css';

interface ServiceGroupMembersProps {
  serviceGroupId: string;
  /** Shows the add and remove actions; the backend enforces it too. */
  isAdmin: boolean;
  /** Only these can be added; undefined while loading. */
  teamMemberEntraIds: string[] | undefined;
}

export const ServiceGroupMembers = ({
  serviceGroupId,
  isAdmin,
  teamMemberEntraIds,
}: ServiceGroupMembersProps) => {
  const sonarqubeApi = useApi(sonarqubeApiRef);
  const [refreshToken, setRefreshToken] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<MemberToRemove>();
  const groups = useAsync(
    () => sonarqubeApi.getGroups(serviceGroupId),
    [sonarqubeApi, serviceGroupId, refreshToken],
  );
  const users = useCatalogUserOptions();

  const nameByEntraId = useMemo(
    () =>
      new Map(
        (users.value ?? []).flatMap(u =>
          u.entraId ? [[u.entraId, u.label] as const] : [],
        ),
      ),
    [users.value],
  );
  // The backend refuses to remove a service group's only admin, so don't offer it.
  const lastAdminEntraId = useMemo(() => {
    const admins = (groups.value ?? [])
      .filter(g => g.name.endsWith('_admin'))
      .flatMap(g => g.users.filter(u => u.isAuthorized && u.entraId));
    return admins.length === 1 ? admins[0].entraId : undefined;
  }, [groups.value]);
  const existingUsers = useMemo(
    () =>
      new Set(
        (groups.value ?? []).flatMap(g =>
          g.users.flatMap(u => (u.entraId ? [u.entraId] : [])),
        ),
      ),
    [groups.value],
  );

  const handleAdded = useCallback(() => {
    setAddOpen(false);
    setRefreshToken(t => t + 1);
  }, []);
  const handleRemoved = useCallback(() => {
    setMemberToRemove(undefined);
    setRefreshToken(t => t + 1);
  }, []);

  const renderGroups = () => {
    if (groups.loading) {
      return (
        <div className={styles.skeletons}>
          <Skeleton width="100%" height={40} />
          <Skeleton width="100%" height={40} />
        </div>
      );
    }
    if (groups.error) {
      return <ResponseErrorPanel error={groups.error} />;
    }
    return (
      <AccordionGroup allowsMultiple>
        {(groups.value ?? []).map(group => (
          <Accordion key={group.id}>
            <AccordionTrigger
              title={group.name.endsWith("admin") ? "Admins" : "Developers"}
              subtitle={`${group.users.length} ${
                group.users.length === 1 ? 'member' : 'members'
              }`}
            />
            <AccordionPanel>
              {group.users.length === 0 && (
                <Text color="secondary">No members.</Text>
              )}
              {group.users.map(user => {
                const name =
                  (user.entraId && nameByEntraId.get(user.entraId)) ||
                  user.email;
                return (
                  <div key={user.id} className={styles.memberRow}>
                    {users.loading ? (
                      <Skeleton width={160} height={20} />
                    ) : (
                      <Text>{name}</Text>
                    )}
                    <Text color="secondary">{user.email}</Text>
                    <span className={styles.memberRemove}>
                      {isAdmin &&
                        user.entraId &&
                        user.entraId !== lastAdminEntraId && (
                          <ButtonIcon
                            aria-label={`Remove ${name}`}
                            variant="tertiary"
                            icon={<DeleteIcon fontSize="small" />}
                            onPress={() =>
                              setMemberToRemove({
                                entraId: user.entraId!,
                                name,
                              })
                            }
                          />
                        )}
                    </span>
                    {!user.isAuthorized && (
                      <Alert
                        className={styles.memberAlert}
                        status="danger"
                        icon
                        description="Added or changed directly in SonarQube, so this access isn't backed by the Developer Platform."
                      />
                    )}
                  </div>
                );
              })}
            </AccordionPanel>
          </Accordion>
        ))}
      </AccordionGroup>
    );
  };

  return (
    <SectionCard
      title="Members"
      action={
        isAdmin ? (
          <Button
            iconStart={<AddCircleIcon />}
            onClick={() => setAddOpen(true)}
          >
            Add group member
          </Button>
        ) : undefined
      }
    >
      {renderGroups()}
      <AddGroupMemberDialog
        open={addOpen}
        serviceGroupId={serviceGroupId}
        userOptions={users.value}
        loadingUsers={users.loading || !teamMemberEntraIds}
        existingEntraIds={existingUsers}
        teamMemberEntraIds={teamMemberEntraIds}
        onClose={() => setAddOpen(false)}
        onAdded={handleAdded}
      />
      <RemoveGroupMemberDialog
        serviceGroupId={serviceGroupId}
        member={memberToRemove}
        onClose={() => setMemberToRemove(undefined)}
        onRemoved={handleRemoved}
      />
    </SectionCard>
  );
};
